/* eslint-disable custom-rules/data-testid-required */
import React, { useEffect, useRef, useState, useMemo } from "react";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  Grid,
  GridItem,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Select,
  useDisclosure,
} from "@chakra-ui/react";
import { ChevronDownIcon } from "@heroicons/react/outline";
import propTypes from "prop-types";
import { DateTime } from "luxon";

import {
  appliedDateFormat,
  shorthandOperators,
  appliedTimeFormat,
  timeRanges,
} from "./DateTimeDefault";
import useDateTimeRange from "../../hooks/useDateTimeRange";

const DateTimeRange = ({
  defaultValue = { timeRange: timeRanges[0].value },
  id,
  isDisabled,
  name,
  onChange,
  size,
  value,
  variant,
  ...rest
}) => {
  // State Hooks
  const [shorthandValue, setShorthandValue] = useState(defaultValue?.timeRange);
  const [appliedValues, setAppliedValues] = useState(
    defaultValue ??
      value ??
      shorthandOperators[defaultValue.timeRange](
        appliedDateFormat,
        appliedTimeFormat
      )
  );

  // Utility Hooks
  const { setValue, setValues, error, isError, ...dateTimeValues } =
    useDateTimeRange({
      dateFormat: appliedDateFormat,
      shorthandOperators,
      timeFormat: appliedTimeFormat,
    });

  const initialFocusRef = useRef();
  const { isOpen, onClose, onOpen } = useDisclosure();

  // Keeps the component in a controlled state if there is a "value" prop present
  useEffect(() => {
    if (value) {
      const { timeRange, ...rest } = value;
      setShorthandValue(timeRange);
      setValues(rest);
      setAppliedValues(value);
    }
  }, [value]);

  // Event Handlers
  const handleDateTimeChange = (eventValue, key) => {
    setValue(key, eventValue);
    setShorthandValue("custom");
  };

  const handleDateTimeOptionChange = (eventValue) => {
    if (Object.keys(shorthandOperators).includes(eventValue)) {
      setValue(eventValue);
    }

    setShorthandValue(eventValue);
  };

  const handleApply = () => {
    const compiledValues = {
      ...dateTimeValues,
      timeRange: shorthandValue,
    };

    onClose();
    setAppliedValues(compiledValues);

    if (typeof onChange === "function") {
      onChange(compiledValues);
    }
  };

  const handleCloseWithoutApply = () => {
    onClose();
    setShorthandValue(appliedValues.timeRange);

    setValues({
      endDate: appliedValues.endDate,
      endTime: appliedValues.endTime,
      startDate: appliedValues.startDate,
      startTime: appliedValues.startTime,
    });
  };

  const handleOpen = () => {
    handleDateTimeOptionChange(appliedValues.timeRange);
    onOpen();
  };

  const formatDisplayValue = () => {
    if (appliedValues.timeRange === "custom") {
      const endDateTime = DateTime.fromFormat(
        `${appliedValues.endDate} ${appliedValues.endTime}`,
        `${appliedDateFormat} ${appliedTimeFormat}`
      );

      const startDateTime = DateTime.fromFormat(
        `${appliedValues.startDate} ${appliedValues.startTime}`,
        `${appliedDateFormat} ${appliedTimeFormat}`
      );

      return `${startDateTime.toFormat("f")} - ${endDateTime.toFormat("f")}`;
    } else {
      return timeRanges.find((t) => t.value === appliedValues.timeRange).label;
    }
  };

  // Utility function to filter out non-DOM props before passing to DOM elements
  const filterDomProps = useMemo(() => {
    // Extract startDateMin and any other non-DOM props
    const { startDateMin, ...domProps } = rest;
    return domProps;
  }, [rest]);

  return (
    <Popover
      computePositionOnMount
      initialFocusRef={initialFocusRef}
      isOpen={isOpen}
      onClose={handleCloseWithoutApply}
      placement="bottom-end"
    >
      <PopoverTrigger>
        <InputGroup
          onClick={!isDisabled ? handleOpen : null}
          {...filterDomProps}
        >
          <Input
            cursor={isDisabled ? "not-allowed" : "pointer"}
            id={id}
            isDisabled={isDisabled}
            isReadOnly
            name={name}
            size={size}
            value={formatDisplayValue()}
            variant={variant}
            style={{ textOverflow: "ellipsis" }}
          />

          <InputRightElement>
            <IconButton
              isDisabled={isDisabled}
              icon={<Icon as={ChevronDownIcon} />}
              variant="unstyled"
            />
          </InputRightElement>
        </InputGroup>
      </PopoverTrigger>

      <PopoverContent width="25rem">
        <PopoverArrow />

        <PopoverHeader>
          Select Time Range
          <PopoverCloseButton />
        </PopoverHeader>

        <PopoverBody>
          <Grid gap="2" templateColumns="1fr 1fr">
            <GridItem colSpan={2}>
              {isError && (
                <Alert status="error">
                  <AlertIcon />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <FormControl>
                <FormLabel>Time Range</FormLabel>

                <Select
                  onChange={(e) => handleDateTimeOptionChange(e.target.value)}
                  ref={initialFocusRef}
                  value={shorthandValue}
                >
                  {timeRanges.map((timeRange) => (
                    <option key={timeRange.value} value={timeRange.value}>
                      {timeRange.label}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </GridItem>

            <FormControl isInvalid={!dateTimeValues.startDate}>
              <FormLabel>Start Date</FormLabel>

              <Input
                min={
                  rest.startDateMin
                    ? DateTime.fromISO(dateTimeValues.startDate)
                        .minus(rest.startDateMin)
                        .toISODate()
                    : null
                }
                max={dateTimeValues.endDate}
                onChange={(e) =>
                  handleDateTimeChange(e.target.value, "startDate")
                }
                type="date"
                value={dateTimeValues.startDate}
              />

              <FormErrorMessage>Invalid Value</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!dateTimeValues.endDate}>
              <FormLabel>End Date</FormLabel>

              <Input
                min={dateTimeValues.startDate}
                onChange={(e) =>
                  handleDateTimeChange(e.target.value, "endDate")
                }
                type="date"
                value={dateTimeValues.endDate}
              />

              <FormErrorMessage>Invalid Value</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!dateTimeValues.startTime}>
              <FormLabel>Start Time</FormLabel>

              <Input
                onChange={(e) =>
                  handleDateTimeChange(e.target.value, "startTime")
                }
                type="time"
                value={dateTimeValues.startTime}
              />

              <FormErrorMessage>Invalid Value</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!dateTimeValues.endTime}>
              <FormLabel>End Time</FormLabel>

              <Input
                onChange={(e) =>
                  handleDateTimeChange(e.target.value, "endTime")
                }
                type="time"
                value={dateTimeValues.endTime}
              />

              <FormErrorMessage>Invalid Value</FormErrorMessage>
            </FormControl>

            <GridItem colStart={2}>
              <Button
                isDisabled={isError}
                isFullWidth
                onClick={handleApply}
                variant="primary"
              >
                Apply
              </Button>
            </GridItem>
          </Grid>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

DateTimeRange.displayName = "DateTimeRange";

DateTimeRange.propTypes = {
  defaultValue: propTypes.shape({
    endDate: propTypes.string,
    endTime: propTypes.string,
    startDate: propTypes.string,
    startTime: propTypes.string,
    timeRange: propTypes.string,
  }),
  id: propTypes.string,
  isDisabled: propTypes.bool,
  name: propTypes.string,
  onChange: propTypes.func,
  size: propTypes.string,
  value: propTypes.shape({
    endDate: propTypes.string,
    endTime: propTypes.string,
    startDate: propTypes.string,
    startTime: propTypes.string,
    timeRange: propTypes.string,
  }),
  variant: propTypes.string,
};

export default DateTimeRange;
